import { handleError } from '../../features/layout/layoutSlice';
import { store } from '../store';

export const file_system_access_supported = 'showSaveFilePicker' in window;

export function getFileFromBase64(fileName: string, type: string, content: string) {
  const sliceSize = 1024;
  const byteCharacters = atob(content);
  const bytesLength = byteCharacters.length;
  const slicesCount = Math.ceil(bytesLength / sliceSize);
  const byteArrays = new Array(slicesCount);

  for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
    const begin = sliceIndex * sliceSize;
    const end = Math.min(begin + sliceSize, bytesLength);

    const bytes = new Array(end - begin);
    for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
      bytes[i] = byteCharacters[offset].charCodeAt(0);
    }
    byteArrays[sliceIndex] = new Uint8Array(bytes);
  }
  const blob = new Blob(byteArrays, { type: type });
  return new File([blob], fileName);
}

export const convertFileToBase64 = (file: File) => {
  return new Promise(resolve => {
    var reader = new FileReader();
    reader.onload = (event: ProgressEvent<FileReader>) => {
      if (event.target) {
        resolve(event.target?.result);
      }
    };
    reader.readAsDataURL(file);
  });
};

export const downloadFile = (file: File) => {
  const blobURL = window.URL.createObjectURL(file);
  const tempLink = document.createElement('a');
  tempLink.style.display = 'none';
  tempLink.href = blobURL;
  tempLink.setAttribute('download', file.name);

  if (typeof tempLink.download === 'undefined') {
    tempLink.setAttribute('target', '_blank');
  }
  document.body.appendChild(tempLink);
  tempLink.click();
  document.body.removeChild(tempLink);
  setTimeout(() => {
    window.URL.revokeObjectURL(blobURL);
  }, 100);

};

export async function saveFileWithPicker(file: File, fileName?: string) {
  try {
    if (file_system_access_supported) {
      const handle = await window.showSaveFilePicker({ suggestedName: fileName ?? file.name });
      const writable = await handle.createWritable();
      await writable.write(file);
      await writable.close();
      return handle;
    }
    else downloadFile(file);
  } catch (err: any) {
    if (err.name === 'AbortError') {
      return;
    } else {
      console.error(err.name, err);
    }
  }
}


export const verifyFileSystemFileHandlePermission = async (fileHandle: FileSystemFileHandle, readWrite: boolean) => {
  const options: FileSystemHandlePermissionDescriptor = {};
  if (!file_system_access_supported) return false;

  if (readWrite) {
    options.mode = 'read';
  }
  if ((await fileHandle.queryPermission(options)) === 'granted') {
    return true;
  }

  if ((await fileHandle.requestPermission(options)) === 'granted') {
    return true;
  }
  return false;
};

type ChangedFileState = { changed: boolean, file?: File };

export async function checkFileForChanges(fileHandle: FileSystemFileHandle, lastModified: number): Promise<ChangedFileState> {
  if (fileHandle) {
    try {
      const allowed = await verifyFileSystemFileHandlePermission(fileHandle, true);
      if (allowed) {
        const file = await fileHandle.getFile();
        if (lastModified !== file.lastModified) return ({ changed: true, file });
        else return ({ changed: false });
      }
      return ({ changed: false });
    } catch (err: any) {
      store.dispatch(handleError(err));
    }
  }

  return ({ changed: false });
}


export const handleOpenFilePicker = async (): Promise<File | undefined> => {
  try {
    const [fileHandle] = await window.showOpenFilePicker();
    const file = await fileHandle.getFile();
    if (file) return file;
  } catch (err: any) {
    if (err.name === 'AbortError') {
      return;
    } else {
      console.error(err.name, err);
    }
  }
};
